import React, { useEffect, useState } from 'react';
import { Tabs, Tab, Box, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import { dashboardStyles } from '../styles/dashboard';
import { TabPanelProps, DashboardPageProps, MonthlyData, ItemSalesData, CompanyMonthlyData } from '../types/dashboard';
import SalesTab from './dashboard/tabs/SalesTab';
import CompanySalesTab from './dashboard/tabs/CompanySalesTab';
import cachedApi from '../utils/cachedApi';

// dayjs 한글 설정
dayjs.locale('ko');

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
      style={{ 
        flex: 1,
        display: value === index ? 'flex' : 'none',
        flexDirection: 'column',
        overflow: 'auto',
        height: '100%',
        paddingTop: '16px'
      }}
    >
      {children}
    </div>
  );
}

const DashboardPage: React.FC<DashboardPageProps> = ({ isFullScreen, setIsFullScreen }) => {
  const [tabValue, setTabValue] = useState(0);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [itemSalesData, setItemSalesData] = useState<ItemSalesData[]>([]);
  const [loading, setLoading] = useState(false);
  const [itemSalesLoading, setItemSalesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [itemSalesError, setItemSalesError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState(() => {
    const end = dayjs();
    const start = dayjs().subtract(14, 'month');
    return { start, end };
  });
  const [companyMonthlyData, setCompanyMonthlyData] = useState<CompanyMonthlyData[]>([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDateChange = (type: 'start' | 'end', value: Dayjs | null) => {
    if (!value) return;
    setDateRange(prev => ({ ...prev, [type]: value }));
  };

  const fetchMonthlyData = async (start: string, end: string) => {
    setLoading(true);
    setError(null);
    try {
      // 캐시된 API 사용
      // const data = await cachedApi.getMonthlyBilling();
      // setMonthlyData(data);
      // ↓ 항상 fetch로 직접 조회
      const res = await fetch(`/api/dashboard/monthly-billing?start=${start}&end=${end}`);
      if (!res.ok) throw new Error('데이터를 불러오지 못했습니다');
      const data = await res.json();
      setMonthlyData(data);
    } catch (e: any) {
      console.error('월별 매출 데이터 조회 실패:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchItemSales = async (start: string, end: string) => {
    setItemSalesLoading(true);
    setItemSalesError(null);
    try {
      // 캐시된 API 사용
      // const data = await cachedApi.getItemSales();
      // setItemSalesData(data);
      // ↓ 항상 fetch로 직접 조회
      const res = await fetch(`/api/dashboard/item-sales?start=${start}&end=${end}`);
      if (!res.ok) throw new Error('데이터를 불러오지 못했습니다');
      const data = await res.json();
      setItemSalesData(data);
    } catch (e: any) {
      console.error('품목별 매출 데이터 조회 실패:', e);
      setItemSalesError(e.message);
    } finally {
      setItemSalesLoading(false);
    }
  };

  const fetchCompanyMonthlyData = async (start: string, end: string) => {
    setCompanyLoading(true);
    setCompanyError(null);
    try {
      // 캐시된 API 사용
      // const data = await cachedApi.getMonthlyCompanyBilling();
      // setCompanyMonthlyData(data);
      // ↓ 항상 fetch로 직접 조회
      const res = await fetch(`/api/dashboard/monthly-company-billing?start=${start}&end=${end}`);
      if (!res.ok) throw new Error('데이터를 불러오지 못했습니다');
      const data = await res.json();
      setCompanyMonthlyData(data);
    } catch (e: any) {
      console.error('거래처별 매출 데이터 조회 실패:', e);
      setCompanyError(e.message);
    } finally {
      setCompanyLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const startDate = dateRange.start.startOf('month').format('YYYY-MM-DD');
        const endDate = dateRange.end.endOf('month').format('YYYY-MM-DD');
        if (tabValue === 1) {
          await fetchCompanyMonthlyData(startDate, endDate);
        } else if (tabValue === 0) {
          await fetchMonthlyData(startDate, endDate);
          await fetchItemSales(startDate, endDate);
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tabValue, dateRange.start.valueOf(), dateRange.end.valueOf()]);

  return (
    <Box sx={dashboardStyles.container}>
      {/* 헤더 영역 */}
      <Box sx={dashboardStyles.header}>
        <Typography variant="h4" sx={dashboardStyles.title}>
        
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              mr: 2, // 오른쪽 여백 추가
              '& .MuiTab-root': {
                fontSize: '0.95rem',
                fontWeight: 500,
                color: '#999',
                textTransform: 'none',
                minWidth: 80,
                '&.Mui-selected': {
                  fontWeight: 600,
                  color: '#fff'
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#fff'
              }
            }}
          >
            <Tab label="매출" />
            <Tab label="거래처매출" />
            <Tab label="배차" />
            <Tab label="정산" />
          </Tabs>

        </Box>
      </Box>

      {/* 메인 컨텐츠 영역 */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        overflow: 'auto', // 메인 컨텐츠 스크롤 허용
        minHeight: 0 // 부모 고정 높이에서 내부 스크롤 작동
      }}>
        <CustomTabPanel value={tabValue} index={0}>
          <SalesTab
            dateRange={dateRange}
            onDateChange={handleDateChange}
            monthlyData={monthlyData}
            itemSalesData={itemSalesData}
            loading={loading}
            itemSalesLoading={itemSalesLoading}
            error={error}
            itemSalesError={itemSalesError}
          />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={1}>
          <CompanySalesTab
            dateRange={dateRange}
            onDateChange={handleDateChange}
            companyMonthlyData={companyMonthlyData}
            loading={companyLoading}
            error={companyError}
            selectedCompany={selectedCompany}
            onCompanyChange={setSelectedCompany}
          />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={2}>
          <Box sx={{ p: 2, color: '#fff' }}>
            배차 대시보드 (추후 구현 예정)
          </Box>
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={3}>
          <Box sx={{ p: 2, color: '#fff' }}>
            정산 대시보드 (추후 구현 예정)
          </Box>
        </CustomTabPanel>
      </Box>
    </Box>
  );
};

export default DashboardPage; 